import { useState, useMemo } from "react";
import { parseFechaLocal, formatPorcionesOCantidad } from "../utils/pedidoDisplay";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const DIAS_SEMANA = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export default function CalendarioEntregas({ pedidos, onDiaClick }) {
  const hoy = new Date();
  const [mesActual, setMesActual] = useState(hoy.getMonth());
  const [anioActual, setAnioActual] = useState(hoy.getFullYear());
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  const pedidosPorDia = useMemo(() => {
    const mapa = {};
    pedidos.forEach((p) => {
      if (!p.fecha_entrega) return;
      const fecha = parseFechaLocal(p.fecha_entrega);
      const key = `${fecha.getFullYear()}-${fecha.getMonth()}-${fecha.getDate()}`;
      if (!mapa[key]) mapa[key] = [];
      mapa[key].push(p);
    });
    return mapa;
  }, [pedidos]);

  const diasDelMes = useMemo(() => {
    const primerDia = new Date(anioActual, mesActual, 1);
    const ultimoDia = new Date(anioActual, mesActual + 1, 0);
    const dias = [];

    for (let i = 0; i < primerDia.getDay(); i++) {
      dias.push(null);
    }
    for (let d = 1; d <= ultimoDia.getDate(); d++) {
      dias.push(d);
    }
    return dias;
  }, [mesActual, anioActual]);

  const cambiarMes = (delta) => {
    let nuevoMes = mesActual + delta;
    let nuevoAnio = anioActual;
    if (nuevoMes < 0) {
      nuevoMes = 11;
      nuevoAnio--;
    } else if (nuevoMes > 11) {
      nuevoMes = 0;
      nuevoAnio++;
    }
    setMesActual(nuevoMes);
    setAnioActual(nuevoAnio);
    setDiaSeleccionado(null);
  };

  const esHoy = (dia) =>
    dia === hoy.getDate() && mesActual === hoy.getMonth() && anioActual === hoy.getFullYear();

  const handleClickDia = (dia) => {
    if (!dia) return;
    const key = `${anioActual}-${mesActual}-${dia}`;
    setDiaSeleccionado(diaSeleccionado === key ? null : key);
    if (onDiaClick) {
      onDiaClick(pedidosPorDia[key] || [], new Date(anioActual, mesActual, dia));
    }
  };

  const pedidosDelDiaSeleccionado = diaSeleccionado ? pedidosPorDia[diaSeleccionado] || [] : [];

  const fechaSeleccionadaLegible = useMemo(() => {
    if (!diaSeleccionado) return null;
    const [anio, mes, dia] = diaSeleccionado.split("-").map(Number);
    const fecha = new Date(anio, mes, dia);
    return fecha.toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" });
  }, [diaSeleccionado]);

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="w-full lg:w-[340px] flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E9F0] hover:bg-[#F1F4F9] text-gray-600"
          >
            ‹
          </button>
          <h3 className="text-sm font-semibold text-gray-900">
            {MESES[mesActual]} {anioActual}
          </h3>
          <button
            onClick={() => cambiarMes(1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E9F0] hover:bg-[#F1F4F9] text-gray-600"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {diasDelMes.map((dia, i) => {
            if (!dia) return <div key={i} />;
            const key = `${anioActual}-${mesActual}-${dia}`;
            const pedidosDia = pedidosPorDia[key] || [];
            const seleccionado = diaSeleccionado === key;
            const hayPendientes = pedidosDia.some((p) => p.estado === "pendiente");
            const todosEntregados = pedidosDia.length > 0 && pedidosDia.every((p) => p.estado === "listo");
            const todosCancelados = pedidosDia.length > 0 && pedidosDia.every((p) => p.estado === "cancelado");

            return (
              <button
                key={i}
                onClick={() => handleClickDia(dia)}
                className={`aspect-square rounded-lg text-xs font-medium relative flex flex-col items-center justify-center transition-all ${
                  seleccionado
                    ? "bg-[#1A56DB] text-white"
                    : esHoy(dia)
                    ? "bg-blue-50 text-[#1A56DB] border border-[#1A56DB]"
                    : pedidosDia.length > 0
                    ? "bg-[#F1F4F9] text-gray-900 hover:bg-gray-100"
                    : "text-gray-400 hover:bg-[#F1F4F9]"
                }`}
              >
                <span>{dia}</span>
                {pedidosDia.length > 0 && (
                  <span
                    className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                      seleccionado
                        ? "bg-white"
                        : hayPendientes
                        ? "bg-[#1A56DB]"
                        : todosEntregados
                        ? "bg-green-500"
                        : todosCancelados
                        ? "bg-red-400"
                        : "bg-gray-400"
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 lg:border-l lg:border-[#E5E9F0] lg:pl-4 min-w-0">
        {!diaSeleccionado && (
          <div className="h-full flex items-center justify-center text-center text-sm text-gray-400 py-12">
            Selecciona un día en el calendario<br />para ver los pedidos agendados
          </div>
        )}

        {diaSeleccionado && (
          <>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              {pedidosDelDiaSeleccionado.length} pedido{pedidosDelDiaSeleccionado.length !== 1 ? "s" : ""}
            </h4>
            <p className="text-sm font-medium text-gray-900 capitalize mb-3">
              {fechaSeleccionadaLegible}
            </p>

            {pedidosDelDiaSeleccionado.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-8 bg-[#F1F4F9] rounded-lg border border-dashed border-gray-300">
                Sin entregas este día
              </div>
            )}

            <div className="space-y-2">
              {pedidosDelDiaSeleccionado
                .sort((a, b) => (a.hora_entrega || "").localeCompare(b.hora_entrega || ""))
                .map((p) => {
                  const estadoLabel =
                    p.estado === "pendiente" ? "Pendiente" : p.estado === "listo" ? "Entregado" : "Cancelado";
                  const estadoColor =
                    p.estado === "pendiente"
                      ? "text-amber-700 bg-amber-50 border-amber-200"
                      : p.estado === "listo"
                      ? "text-green-700 bg-green-50 border-green-200"
                      : "text-red-600 bg-red-50 border-red-200";
                  const saldo = parseInt(p.saldo || 0);

                  return (
                    <div key={p.id} className="bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-[#1A56DB]">{p.numero_pedido}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${estadoColor}`}>
                            {estadoLabel}
                          </span>
                          <span className="text-xs text-gray-500">{p.hora_entrega?.slice(0, 5)} hrs</span>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900">{p.cliente_nombre}</div>
                      <div className="text-xs text-gray-500">
                        {p.producto} · {formatPorcionesOCantidad(p)} · ${parseInt(p.precio_total).toLocaleString("es-CL")}
                      </div>
                      {saldo > 0 && (
                        <div className="text-xs text-amber-600 font-medium mt-0.5">
                          Saldo: ${saldo.toLocaleString("es-CL")}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
